// Use the browser entry (`index`), not Node `entry-node`, which dynamically
// requires every locale JSON and breaks Rollup/Astro SSG bundling.
import countries from "i18n-iso-countries/index.js";
import en from "i18n-iso-countries/langs/en.json";

countries.registerLocale(en);

/** ISO 3166-1 alpha-2 country code, lowercase (e.g. `nz`, `au`, `gb`). */
export type CountryCode = string;

/** Billing/settings region keys used by accounts.region. */
export type BillingRegion = "nz" | "au" | "uk" | "us" | "eu" | "global";

const EU_COUNTRY_CODES = new Set([
  "at",
  "be",
  "bg",
  "hr",
  "cy",
  "cz",
  "dk",
  "ee",
  "fi",
  "fr",
  "de",
  "gr",
  "hu",
  "ie",
  "it",
  "lv",
  "lt",
  "lu",
  "mt",
  "nl",
  "pl",
  "pt",
  "ro",
  "sk",
  "si",
  "es",
  "se",
]);

/**
 * Map an ISO country code to the account billing region.
 * `gb` and legacy `uk` both map to `uk`.
 */
export function countryToRegion(code: string | null | undefined): BillingRegion {
  const c = normalizeCountryCode(code);
  if (!c) return "global";
  if (c === "nz") return "nz";
  if (c === "au") return "au";
  if (c === "gb" || c === "uk") return "uk";
  if (c === "us") return "us";
  if (EU_COUNTRY_CODES.has(c)) return "eu";
  return "global";
}

/** Normalize to lowercase alpha-2, or null if invalid. */
export function normalizeCountryCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const c = code.trim().toLowerCase();
  if (c.length !== 2 || !/^[a-z]{2}$/.test(c)) return null;
  return c;
}

/** Regional indicator emoji flag for an ISO alpha-2 code (e.g. `nz` → 🇳🇿). */
export function flagEmojiFromCountryCode(code: string | null | undefined): string {
  const c = normalizeCountryCode(code);
  if (!c) return "";
  const upper = c.toUpperCase();
  return String.fromCodePoint(...[...upper].map((ch) => 0x1f1e6 - 65 + ch.charCodeAt(0)));
}

/**
 * Extract a country code from a BCP-47 locale (e.g. `en-NZ` → `nz`).
 * Returns null when no Unicode region subtag is present.
 */
export function countryCodeFromLocale(locale: string | null | undefined): string | null {
  if (!locale) return null;
  const normalized = locale.trim().replace(/_/g, "-");
  try {
    const region = new Intl.Locale(normalized).region;
    return normalizeCountryCode(region ?? null);
  } catch {
    const parts = normalized.split("-");
    for (let i = parts.length - 1; i >= 1; i--) {
      const part = parts[i];
      if (part.length === 2 && /^[A-Za-z]{2}$/.test(part)) {
        return normalizeCountryCode(part);
      }
    }
    return null;
  }
}

export interface CountryOption {
  code: string;
  name: string;
  flag: string;
}

let cachedCountries: CountryOption[] | null = null;

/** Full country list for selects (English official names). */
export function getCountryOptions(): CountryOption[] {
  if (cachedCountries) return cachedCountries;

  const names = countries.getNames("en", { select: "official" });
  cachedCountries = Object.entries(names)
    .map(([code, name]) => {
      const lower = code.toLowerCase();
      return {
        code: lower,
        name,
        flag: flagEmojiFromCountryCode(lower),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return cachedCountries;
}

export function getCountryName(code: string | null | undefined): string {
  const c = normalizeCountryCode(code);
  if (!c) return code ?? "";
  const name = countries.getName(c.toUpperCase(), "en", { select: "official" });
  return name || c.toUpperCase();
}

/**
 * Club directory / clubs.country still use English display names.
 * Prefer the official English name for a known ISO code; pass through non-ISO values.
 */
export function countryCodeToDisplayName(codeOrName: string | null | undefined): string | null {
  if (!codeOrName) return null;
  const c = normalizeCountryCode(codeOrName);
  if (!c) return codeOrName.trim() || null;
  return getCountryName(c);
}

/**
 * Clubs must match the account country. Directory currently only supports NZ listings,
 * so non-NZ accounts map to the club country value "Other".
 */
export function clubCountryForAccount(account: {
  country?: string | null;
  region?: string | null;
}): "New Zealand" | "Other" {
  if (account.country === "nz" || account.region === "nz") return "New Zealand";
  return "Other";
}

/** Filter countries by name or ISO code (case-insensitive). */
export function filterCountryOptions(options: CountryOption[], search: string): CountryOption[] {
  const q = search.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.name.toLowerCase().includes(q) || o.code.includes(q));
}
